/**
 * THE ONE FILE MS4 REPLACED, on the operator artifact.
 *
 * The twin of the end-user app's resolver, and deliberately a separate file
 * rather than a shared one. This app is a separate artifact precisely so the
 * operator surface can be left unexposed by a reseller and never enters an end
 * user's bundle.
 *
 * MS4 T4.6 (OPSSTD-4691) replaced the body of `resolveSession` with the real
 * read against `operator/auth/sessions/current` — a separate identity plane from
 * the tenant `auth` module's own session, matching that isolation. The
 * contract in `query/query-options.ts` and every surface stayed as they were.
 *
 * WHY THIS CONTRACT HAS NO ROLE, WHERE THE CLIENT'S HAS ONE. On this artifact
 * a session IS the operator identity — see `query-options.ts` for the full
 * reasoning, not repeated here so the two cannot drift.
 */
import { v1BackofficeAuthGetSession } from '@repo/sdk/gen'

/**
 * The identity the contract carries. `image` stays in the shape even though
 * the backoffice profile has no avatar field yet — a field a consumer drops
 * is a field a future backend addition discovers is missing from every
 * surface, same reasoning the client app's resolver documents.
 */
interface SessionUser {
	id: string
	name: string
	email: string
	image: { url: string } | null
}

/**
 * The operator is cross-tenant, so "current organization" reads oddly here —
 * and it is still right. The contract makes an authenticated session without
 * an organization unrepresentable, and the operator does belong to one: the
 * instance's own, the reseller's. `operator/auth/sessions/current` carries no
 * organization of its own (there is no per-tenant concept on this identity
 * plane), so this is the instance's product name, which the caller resolves
 * from the runtime branding (`lib/brand.ts`) and passes as an empty string when
 * none is published — nothing here draws it.
 */
export async function resolveSession(instanceName: string) {
	const session = await v1BackofficeAuthGetSession({})

	if (session.sessionType !== 'authenticated' || !session.user) {
		return { sessionType: 'anonymous' as const }
	}

	const user: SessionUser = {
		id: session.user.id,
		name: session.user.name,
		email: session.user.email ?? '',
		image: null
	}

	return {
		sessionType: 'authenticated' as const,
		user,
		organization: { id: 'instance', name: instanceName },
		csrfToken: session.csrfToken
	}
}
